/**
 * ListViewSwitcher — wybór EDYTOWANEGO widoku listy w nagłówku Kreatora Listy każdej kartoteki:
 * systemowej i modułu (wzór: wybór layoutu w Kreatorze formularza, `LayoutSwitcher`).
 *
 * Przycisk pokazuje nazwę edytowanego widoku (+ „domyślny" / „tylko do odczytu"); rozwinięta
 * lista = wszystkie widoki listy kartoteki z akcjami: gwiazdka „ustaw jako domyślny", zmiana
 * nazwy, duplikat, eksport do pliku, usunięcie; na dole „Nowy widok" i „Importuj widok z pliku…".
 *
 * Który widok jest edytowany, decyduje rodzic (`?layout=<id>` w adresie kreatora →
 * sufiks `::layout-<id>` w `tableId`, ten sam dla wszystkich paneli); komponent zgłasza wybór
 * przez `onSelect` (null = wróć do widoku domyślnego). Widok centralny (z biblioteki widoków ITE)
 * i widok z menu modułu są tylko do odczytu — zostaje duplikat i eksport, a gwiazdka tylko dla
 * centralnego (serwer pilnuje tego samego: 403 CENTRAL_LAYOUT_READONLY).
 *
 * Trasy: GET/POST na kolekcję widoków kartoteki (`/system-modules/:slug/layouts` albo
 * `/modules/:id/layouts` — wg `tableId`), PUT/DELETE `.../layouts/:id`. Eksport
 * (`.../layouts/:id/export`) i import (`ListViewImportModal`) — tylko kartoteki systemowe.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ListExportFile } from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Copy,
  Download,
  LayoutList,
  Lock,
  Pencil,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { listLayoutKey } from '@/components/configurable-table/active-list-layout';
import type { TableLayout } from '@/components/configurable-table/types';
import {
  collectionEndpointFor,
  isCustomModuleTable,
  useTableLayout,
} from '@/components/configurable-table/useTableLayout';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import { downloadJson, listViewFileName } from './list-view-file';
import { ListViewImportModal } from './ListViewImportModal';
import {
  isReadOnlyListView,
  LIST_VIEW_NAME_MAX,
  listViewErrorKey,
  useCreateListView,
} from './useCreateListView';

const P = 'tenant.listBuilder.views';
/** Szerokość panelu listy widoków (px) i margines od krawędzi okna. */
const PANEL_WIDTH = 380;
const VIEWPORT_GUTTER = 16;

export interface ListViewSwitcherProps {
  /** Slug kartoteki (nazwa pliku eksportu, import widoku kartoteki systemowej). */
  moduleSlug: string;
  /** `tableId` kreatora (z sufiksem `::layout-<id>`, gdy widok wybrany jawnie). */
  tableId: string;
  /** Wybór widoku do edycji; `null` = widok domyślny. */
  onSelect: (layoutId: string | null) => void;
}

type NameDialog = { mode: 'create' } | { mode: 'rename'; layout: TableLayout } | null;

export function ListViewSwitcher({ moduleSlug, tableId, onSelect }: ListViewSwitcherProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { layout: current, availableLayouts, isLoading } = useTableLayout(tableId);
  const hasExplicitView = listLayoutKey(tableId) !== tableId;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [nameDialog, setNameDialog] = useState<NameDialog>(null);
  const [nameValue, setNameValue] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<TableLayout | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Wskazany widok zniknął (usunięty w innej karcie, stary link) → wracamy do domyślnego.
  useEffect(() => {
    if (hasExplicitView && !isLoading && availableLayouts.length > 0 && !current) onSelect(null);
  }, [hasExplicitView, isLoading, availableLayouts.length, current, onSelect]);

  // Panel przez portal (kotwiczony do lewej krawędzi przycisku, dosunięty do okna, gdy by
  // wystawał) — nie chowa się pod treścią kreatora i nie wjeżdża na menu boczne.
  useLayoutEffect(() => {
    if (!open) return;
    const recalc = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(PANEL_WIDTH, window.innerWidth - 2 * VIEWPORT_GUTTER);
      const left = Math.max(
        VIEWPORT_GUTTER,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_GUTTER),
      );
      setPos({ top: rect.bottom + 4, left });
    };
    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const refreshLayouts = () => queryClient.invalidateQueries({ queryKey: ['table-layouts'] });
  // Kartoteka systemowa → `/system-modules/:slug/layouts`, kartoteka modułu → `/modules/:id/layouts`.
  const collection = collectionEndpointFor(tableId);
  // Eksport/import widoku plikiem mają dziś tylko kartoteki systemowe (trasy serwera).
  const transferEnabled = !isCustomModuleTable(tableId);

  function showError(err: unknown) {
    toast.show({ variant: 'error', title: t(listViewErrorKey(err)) });
  }

  const {
    create,
    duplicate,
    isNameTaken,
    isPending: createPending,
  } = useCreateListView({
    collection,
    availableLayouts,
    onSelect,
    onCreated: () => setNameDialog(null),
  });

  const updateMutation = useMutation({
    mutationFn: async (input: {
      layout: TableLayout;
      body: { name?: string; isDefault?: true };
    }) => {
      await api.put(`${collection}/${input.layout.id}`, input.body);
      return input;
    },
    onSuccess: async ({ layout, body }) => {
      await refreshLayouts();
      if (body.name !== undefined) {
        setNameDialog(null);
        toast.show({ variant: 'success', title: t(`${P}.toast.renamed`, { name: body.name }) });
      } else {
        toast.show({
          variant: 'success',
          title: t(`${P}.toast.defaultSet`, { name: layout.name }),
        });
      }
    },
    onError: showError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (layout: TableLayout) => {
      await api.delete(`${collection}/${layout.id}`);
      return layout;
    },
    onSuccess: async (layout) => {
      setToDelete(null);
      if (layout.id === current?.id) onSelect(null);
      await refreshLayouts();
      toast.show({ variant: 'success', title: t(`${P}.toast.deleted`, { name: layout.name }) });
    },
    onError: (err) => {
      setToDelete(null);
      showError(err);
    },
  });

  async function handleExport(layout: TableLayout) {
    try {
      const res = await api.get<ListExportFile>(`${collection}/${layout.id}/export`);
      downloadJson(res.data, listViewFileName(moduleSlug, layout.name));
      toast.show({
        variant: 'success',
        title: t('tenant.listBuilder.transfer.export.success', { name: layout.name }),
      });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.listBuilder.transfer.export.error') });
    }
  }

  function handleSetDefault(layout: TableLayout) {
    // Edytowany widok trzymamy w adresie: bez tego kreator otwarty „na domyślnym" przeskoczyłby
    // po zmianie gwiazdki na nowo wskazany domyślny.
    if (!hasExplicitView && current) onSelect(current.id);
    updateMutation.mutate({ layout, body: { isDefault: true } });
  }

  function openNameDialog(dialog: Exclude<NameDialog, null>) {
    setNameValue(dialog.mode === 'rename' ? dialog.layout.name : '');
    setNameError(null);
    setNameDialog(dialog);
  }

  function submitName() {
    const name = nameValue.trim();
    if (!name) {
      setNameError(t(`${P}.nameDialog.required`));
      return;
    }
    if (name.length > LIST_VIEW_NAME_MAX) {
      setNameError(t(`${P}.nameDialog.tooLong`, { max: LIST_VIEW_NAME_MAX }));
      return;
    }
    if (isNameTaken(name, nameDialog?.mode === 'rename' ? nameDialog.layout.id : undefined)) {
      setNameError(t(`${P}.nameDialog.taken`));
      return;
    }
    if (nameDialog?.mode === 'rename') {
      updateMutation.mutate({ layout: nameDialog.layout, body: { name } });
    } else {
      create(name);
    }
  }

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  const nameBusy = createPending || updateMutation.isPending;

  return (
    <div data-testid="list-view-switcher">
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t(`${P}.triggerAria`, { name: current?.name ?? '' })}
        leftIcon={<LayoutList className="h-4 w-4 text-text-muted" />}
        rightIcon={
          <ChevronDown
            className={cn('h-4 w-4 text-text-muted transition-transform', open && 'rotate-180')}
          />
        }
        data-testid="list-view-switcher-trigger"
      >
        <span className="max-w-[240px] truncate">
          {current?.name ?? (isLoading ? t(`${P}.loading`) : t(`${P}.noViews`))}
        </span>
        {current?.isDefault && (
          <Badge tone="warning" size="sm">
            {t(`${P}.defaultBadge`)}
          </Badge>
        )}
        {current && isReadOnlyListView(current) && (
          <Badge tone="neutral" size="sm" icon={Lock}>
            {t(`${P}.readOnlyBadge`)}
          </Badge>
        )}
      </Button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[9999] max-w-[calc(100vw-32px)] rounded-lg border border-border-primary bg-bg-elevated shadow-lg"
            style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH }}
            data-testid="list-view-switcher-panel"
          >
            <p className="border-b border-border-primary px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              {t(`${P}.heading`)}
            </p>
            <div className="max-h-80 overflow-y-auto py-1">
              {availableLayouts.length === 0 ? (
                <p className="px-3 py-2 text-xs text-text-muted">{t(`${P}.empty`)}</p>
              ) : (
                availableLayouts.map((layout) => {
                  const isCurrent = layout.id === current?.id;
                  const readOnly = isReadOnlyListView(layout);
                  const fromMenu = Boolean(layout.menuSourceModuleId);
                  return (
                    <div
                      key={layout.id}
                      className={cn(
                        'flex items-center gap-1 pr-1.5',
                        isCurrent ? 'bg-accent-50 dark:bg-accent-900/30' : 'hover:bg-bg-secondary',
                      )}
                      data-testid={`list-view-switcher-row-${layout.id}`}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-current={isCurrent ? 'true' : undefined}
                        onClick={() => run(() => onSelect(layout.id))}
                        className={cn(
                          'h-9 min-w-0 flex-1 justify-start rounded-none px-3 hover:bg-transparent',
                          isCurrent && 'text-accent-700 dark:text-accent-300',
                        )}
                        data-testid={`list-view-switcher-option-${layout.id}`}
                      >
                        <span className="truncate">{layout.name}</span>
                        {layout.isDefault && (
                          <Badge tone="warning" size="sm">
                            {t(`${P}.defaultBadge`)}
                          </Badge>
                        )}
                        {readOnly && (
                          <Tooltip
                            content={t(
                              fromMenu ? `${P}.readOnlyMenuHint` : `${P}.readOnlyCentralHint`,
                            )}
                          >
                            <span className="inline-flex">
                              <Badge tone="neutral" size="sm" icon={Lock}>
                                {t(`${P}.readOnlyBadge`)}
                              </Badge>
                            </span>
                          </Tooltip>
                        )}
                      </Button>
                      {!fromMenu && (
                        <Tooltip
                          content={t(layout.isDefault ? `${P}.isDefaultHint` : `${P}.setDefault`)}
                        >
                          <IconButton
                            size="sm"
                            aria-label={t(
                              layout.isDefault ? `${P}.isDefaultHint` : `${P}.setDefault`,
                            )}
                            aria-pressed={layout.isDefault}
                            onClick={() => {
                              if (!layout.isDefault) run(() => handleSetDefault(layout));
                            }}
                            className={layout.isDefault ? 'cursor-default text-warning-500' : ''}
                            data-testid={`list-view-switcher-set-default-${layout.id}`}
                          >
                            <Star className={layout.isDefault ? 'fill-warning-500' : ''} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && (
                        <Tooltip content={t(`${P}.rename`)}>
                          <IconButton
                            size="sm"
                            aria-label={t(`${P}.rename`)}
                            onClick={() => run(() => openNameDialog({ mode: 'rename', layout }))}
                            data-testid={`list-view-switcher-rename-${layout.id}`}
                          >
                            <Pencil />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip content={t(`${P}.duplicate`)}>
                        <IconButton
                          size="sm"
                          aria-label={t(`${P}.duplicate`)}
                          onClick={() => run(() => duplicate(layout))}
                          data-testid={`list-view-switcher-duplicate-${layout.id}`}
                        >
                          <Copy />
                        </IconButton>
                      </Tooltip>
                      {transferEnabled && (
                        <Tooltip content={t(`${P}.export`)}>
                          <IconButton
                            size="sm"
                            aria-label={t(`${P}.export`)}
                            onClick={() => run(() => void handleExport(layout))}
                            data-testid={`list-view-switcher-export-${layout.id}`}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && !layout.isDefault && (
                        <Tooltip content={t(`${P}.delete`)}>
                          <IconButton
                            size="sm"
                            variant="danger"
                            aria-label={t(`${P}.delete`)}
                            onClick={() => run(() => setToDelete(layout))}
                            data-testid={`list-view-switcher-delete-${layout.id}`}
                          >
                            <Trash2 />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="border-t border-border-primary p-1">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => run(() => openNameDialog({ mode: 'create' }))}
                className="w-full justify-start rounded-md text-accent-600"
                data-testid="list-view-switcher-create"
              >
                {t(`${P}.create`)}
              </Button>
              {transferEnabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Upload className="h-4 w-4" />}
                  onClick={() => run(() => setImportOpen(true))}
                  className="w-full justify-start rounded-md text-accent-600"
                  data-testid="list-view-switcher-import"
                >
                  {t(`${P}.import`)}
                </Button>
              )}
            </div>
          </div>,
          document.body,
        )}

      <Modal
        open={nameDialog !== null}
        onClose={() => setNameDialog(null)}
        closeOnBackdrop={!nameBusy}
        closeOnEsc={!nameBusy}
        size="sm"
        title={t(
          nameDialog?.mode === 'rename'
            ? `${P}.nameDialog.renameTitle`
            : `${P}.nameDialog.createTitle`,
        )}
        description={
          nameDialog?.mode === 'create' ? t(`${P}.nameDialog.createDescription`) : undefined
        }
        testId="list-view-name-dialog"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNameDialog(null)} disabled={nameBusy}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submitName}
              loading={nameBusy}
              data-testid="list-view-name-dialog-submit"
            >
              {t(nameDialog?.mode === 'rename' ? `${P}.nameDialog.save` : `${P}.nameDialog.create`)}
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitName();
          }}
        >
          <Input
            label={t(`${P}.nameDialog.label`)}
            value={nameValue}
            onChange={(e) => {
              setNameValue(e.target.value);
              setNameError(null);
            }}
            error={nameError ?? undefined}
            maxLength={LIST_VIEW_NAME_MAX}
            autoFocus
            data-testid="list-view-name-dialog-input"
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        variant="danger"
        title={t(`${P}.deleteDialog.title`)}
        message={t(`${P}.deleteDialog.message`, { name: toDelete?.name ?? '' })}
        confirmLabel={t(`${P}.deleteDialog.confirm`)}
        confirming={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete)}
        onClose={() => setToDelete(null)}
      />

      {transferEnabled && (
        <ListViewImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          moduleSlug={moduleSlug}
          onImported={(layoutId) => {
            void refreshLayouts();
            onSelect(layoutId);
          }}
        />
      )}
    </div>
  );
}
